import { create } from 'zustand';
import { db } from '../lib/database';
import { Role, User } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  permissions: string[];
  enabledFeatures: string[];
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasFeature: (feature: string) => boolean;
  updateUser: (patch: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  user: null,
  permissions: [],
  enabledFeatures: [],
  
  login: async (email: string, password: string) => {
    try {
      console.log('🔐 [AuthStore] Starting login with:', email);
      const response = await db.login(email, password);
      console.log('🔐 [AuthStore] Got response:', response);
      
      if (response && response.id) {
        const user: User = {
          id: String(response.id),
          name: response.name,
          email: response.email,
          position: response.position,
          role: response.role as Role,
          password_changed: response.password_changed || false,
          profile_image: response.profile_image || null,
          is_active: response.is_active !== false
        };

        set({ 
          isAuthenticated: true,
          user,
          permissions: response.permissions || [],
          enabledFeatures: response.enabledFeatures || []
        });
        
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('authToken', response.token || '');
        localStorage.setItem('permissions', JSON.stringify(response.permissions || []));
        localStorage.setItem('enabledFeatures', JSON.stringify(response.enabledFeatures || []));
        
        return true;
      }
      
      return false;
    } catch (error: any) {
      console.error('🔐 [AuthStore] Login error:', error);
      // Propagiraj grešku kako bi UI mogao da prikaže odgovarajuću poruku
      throw error;
    }
  },
  
  logout: () => {
    set({ 
      isAuthenticated: false,
      user: null,
      permissions: [],
      enabledFeatures: []
    });
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('permissions');
    localStorage.removeItem('enabledFeatures');
  },

  checkAuth: async () => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const userJson = localStorage.getItem('user');
    const token = localStorage.getItem('authToken');
    const permissionsJson = localStorage.getItem('permissions');
    const featuresJson = localStorage.getItem('enabledFeatures');
    
    if (isAuthenticated && userJson && token) {
      try {
        const user = JSON.parse(userJson) as User;
        const permissions = permissionsJson ? JSON.parse(permissionsJson) : [];
        const enabledFeatures = featuresJson ? JSON.parse(featuresJson) : [];
        
        set({ 
          isAuthenticated: true, 
          user, 
          permissions, 
          enabledFeatures 
        });
      } catch (e) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        localStorage.removeItem('permissions');
        localStorage.removeItem('enabledFeatures');
      }
    } else {
      // Clear any stale auth data without a valid token
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('permissions');
      localStorage.removeItem('enabledFeatures');
    }
  },
  
  hasPermission: (permission: string) => {
    const { permissions } = get();
    return permissions.includes(permission);
  },
  
  hasFeature: (feature: string) => {
    const { enabledFeatures } = get();
    return enabledFeatures.includes(feature);
  },

  updateUser: (patch: Partial<User>) => {
    const current = get().user;
    if (!current) return;
    const updated = { ...current, ...patch };
    set({ user: updated });
    localStorage.setItem('user', JSON.stringify(updated));
  },
}));

// Initialize auth state on app start
const initAuth = async () => {
  const authStore = useAuthStore.getState();
  await authStore.checkAuth();
};

initAuth();